import { useEffect, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { IntensityIndicator } from "@/components/shared/IntensityIndicator";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import type {
  CommitMismatch,
  PodChangeItem,
  PodComposition,
  PodPositionPreview,
} from "@/hooks/use-staffing-requests";
import { useCommitPreview, useCommitRequest } from "@/hooks/use-staffing-requests";
import {
  buildMismatchAcknowledgementReasons,
  mismatchReasonsComplete,
} from "@/lib/commit-mismatch";
import { INTENSITY_MAX } from "@/lib/intensity";

const POD_SIZES: PodComposition[] = ["Solo", "Pair", "Trio", "Quad"];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestId: string;
  suggestedIntensity: number | null;
  confirmedIntensity: number | null;
  /** Template Start Month — when the suggested Intensity begins (context). */
  startMonth: string | null;
  unmappedCount: number;
}

export function CommitDialog({
  open,
  onOpenChange,
  requestId,
  suggestedIntensity,
  confirmedIntensity,
  startMonth,
  unmappedCount,
}: Props) {
  const defaultIntensity = confirmedIntensity ?? suggestedIntensity ?? 4;
  const [intensity, setIntensity] = useState<number>(defaultIntensity);
  // Per-line acknowledgement reasons, keyed by staffing-request line id. Each
  // Discipline-Mismatch line carries its OWN justification (audit fidelity).
  const [reasons, setReasons] = useState<Record<string, string>>({});
  // null = follow the Solution's current size (server default) until the RM picks one.
  const [podSize, setPodSize] = useState<PodComposition | null>(null);
  const commit = useCommitRequest(requestId);

  const {
    data: preview,
    isLoading: previewLoading,
    isError: previewError,
    error: previewErrorObj,
    refetch: refetchPreview,
  } = useCommitPreview(requestId, open, podSize ?? undefined);

  // Sync intensity default from preview once loaded.
  useEffect(() => {
    if (preview) {
      setIntensity(preview.suggestedIntensity ?? preview.currentIntensity);
    }
  }, [preview]);

  // The size the RM is committing: their explicit choice, else the Solution's current.
  const selectedPodSize: PodComposition | null = podSize ?? preview?.podSize ?? null;
  const hasOverflow = (preview?.overflow.length ?? 0) > 0;
  const hasDuplicates = (preview?.duplicates.length ?? 0) > 0;
  const blockedByMapping = hasOverflow || hasDuplicates;

  const mismatches = preview?.mismatches ?? [];
  const allMismatchReasonsValid = mismatchReasonsComplete(mismatches, reasons);

  function setReason(lineId: string, value: string) {
    setReasons((prev) => ({ ...prev, [lineId]: value }));
  }

  function handleIntensityChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = Number.parseInt(e.target.value, 10);
    if (v >= 1 && v <= INTENSITY_MAX) setIntensity(v);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedPodSize || blockedByMapping || !allMismatchReasonsValid) return;
    const payload: {
      podSize: PodComposition;
      confirmedIntensity: number;
      mismatchAcknowledgementReasons?: Record<string, string>;
    } = { podSize: selectedPodSize, confirmedIntensity: intensity };
    if (mismatches.length > 0) {
      payload.mismatchAcknowledgementReasons = buildMismatchAcknowledgementReasons(
        mismatches,
        reasons,
      );
    }
    commit.mutate(payload, {
      onSuccess: () => {
        setReasons({});
        onOpenChange(false);
      },
      onError: (err: Error) => {
        // Defensive: if a mismatch slipped past the preview (e.g. it went stale),
        // refetch so the per-line acknowledgement prompts appear.
        if (err.message.toLowerCase().includes("discipline mismatch")) {
          refetchPreview();
        }
      },
    });
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      setReasons({});
      setPodSize(null);
      setIntensity(confirmedIntensity ?? suggestedIntensity ?? 4);
    }
    onOpenChange(next);
  }

  const effectiveUnresolved = preview?.unresolvedCount ?? unmappedCount;
  const releasedCount = preview?.releasedCount ?? 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Stage Pod &amp; Commit</DialogTitle>
          <DialogDescription>
            The Pod size is pre-selected from your mapped positions — override if needed. Resources
            you assigned appear below as confirmation and are assigned at commit; positions without
            one are staged Open for the Solution's Pod &amp; Fit view.
            {effectiveUnresolved > 0 && (
              <span className="ml-1 text-muted-foreground">
                {effectiveUnresolved} position(s) will be staged Open.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ── Pod size ───────────────────────────────────────────────────── */}
          <section className="space-y-1.5">
            <Label>Pod size</Label>
            <div className="flex gap-1.5">
              {POD_SIZES.map((size) => {
                const active = selectedPodSize === size;
                return (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setPodSize(size)}
                    className={`flex-1 rounded-md border px-2 py-1.5 text-[12px] font-medium transition-colors ${
                      active
                        ? "border-foreground bg-foreground text-background"
                        : "border-border bg-card text-foreground hover:bg-muted"
                    }`}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-muted-foreground">
              {preview?.suggestedPodSize && podSize === null
                ? `${preview.suggestedPodSize} auto-selected from the mapped positions. `
                : ""}
              Staged into the Solution. The mapped Disciplines define each position's Expected
              Discipline.
            </p>
          </section>

          {/* ── Pod preview ────────────────────────────────────────────────── */}
          {previewLoading && (
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-20 w-full" />
            </div>
          )}

          {previewError && (
            <ErrorState
              message={
                previewErrorObj instanceof Error
                  ? previewErrorObj.message
                  : "Failed to load Pod preview."
              }
              onRetry={() => refetchPreview()}
            />
          )}

          {preview && !previewLoading && (
            <>
              {/* Proposed Pod */}
              <section>
                <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Proposed {preview.podSize} Pod — {preview.solutionName}
                </h3>
                {preview.proposed.length === 0 ? (
                  <p className="text-[12px] text-muted-foreground">
                    No mapped positions — map a Discipline + Position on a line first.
                  </p>
                ) : (
                  <div className="rounded-md border border-border divide-y divide-border">
                    {preview.proposed.map((pos) => (
                      <PodPositionRow key={`${pos.role}-${pos.resourceId ?? "open"}`} pos={pos} />
                    ))}
                  </div>
                )}
              </section>

              {/* Changes vs current Pod */}
              {preview.changes.length > 0 && (
                <section>
                  <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Changes vs current Pod
                  </h3>
                  <div className="space-y-1">
                    {preview.changes.map((c) => (
                      <PodChangeRow
                        key={`${c.role}-${c.type}-${c.toResourceName ?? "open"}`}
                        change={c}
                      />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          {/* ── Intensity ──────────────────────────────────────────────────── */}
          <section className="space-y-1.5">
            <Label htmlFor="intensity-input">
              Intensity (1–{INTENSITY_MAX})
              {preview && (
                <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                  Current: {preview.currentIntensity}
                  {preview.suggestedIntensity !== null &&
                    preview.suggestedIntensity !== preview.currentIntensity && (
                      <span className="ml-1">· Suggested: {preview.suggestedIntensity}</span>
                    )}
                </span>
              )}
              {!preview && suggestedIntensity !== null && (
                <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                  Suggested: {suggestedIntensity}
                </span>
              )}
            </Label>
            <div className="flex items-center gap-3">
              <input
                id="intensity-input"
                type="number"
                min={1}
                max={INTENSITY_MAX}
                value={intensity}
                onChange={handleIntensityChange}
                className="h-9 w-20 rounded-md border border-border bg-card px-3 text-[13px] font-semibold outline-none focus:border-foreground/40"
              />
              <IntensityIndicator level={intensity} size="md" />
            </div>
            <p className="text-[10px] text-muted-foreground">
              RM-confirmed Intensity written to the Solution. Source hours are context only.
              {startMonth && ` The request suggests this Intensity starts ${startMonth}.`}
            </p>
          </section>

          {/* ── Mapping that blocks commit (overflow / duplicates) ──────────── */}
          {preview && hasOverflow && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
              {preview.overflow.length} line(s) map a position beyond a {selectedPodSize} Pod (
              {preview.overflow.map((o) => `${o.role}`).join(", ")}). Pick a larger size or re-map
              them to Supporting.
            </div>
          )}
          {preview && hasDuplicates && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-[12px] text-destructive">
              Each Primary position takes one line — resolve duplicate mappings:{" "}
              {preview.duplicates.map((d) => `${d.role} (${d.count})`).join(", ")}.
            </div>
          )}

          {/* ── Shrinking releases currently-assigned Resources ─────────────── */}
          {releasedCount > 0 && (
            <div className="rounded-md border border-warning-200 bg-warning-50 px-3 py-2 text-[12px] text-warning-700 dark:border-warning-900/40 dark:bg-warning-950/30 dark:text-warning-300">
              Changing to {selectedPodSize} will release {releasedCount} currently-assigned
              Resource(s) whose position no longer fits.
            </div>
          )}

          {/* ── Open positions note (informational, non-blocking) ───────────── */}
          {!blockedByMapping && effectiveUnresolved > 0 && (
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-[12px] text-muted-foreground">
              {effectiveUnresolved} position(s) will be staged Open (no Resource). Assign them later
              in the Solution's Pod &amp; Fit view.
            </div>
          )}

          {/* ── Discipline Mismatch acknowledgement (one reason per line) ────── */}
          {mismatches.length > 0 && (
            <section className="space-y-2">
              <Label>Discipline Mismatch — Acknowledgement Required</Label>
              <p className="text-[11px] text-muted-foreground">
                {mismatches.length === 1
                  ? "One named Resource has a Discipline mismatch. Provide a reason to proceed (min 10 characters)."
                  : `${mismatches.length} named Resources have a Discipline mismatch. Justify each one individually (min 10 characters each).`}
              </p>
              {mismatches.map((m) => (
                <MismatchReasonRow
                  key={m.lineId}
                  mismatch={m}
                  value={reasons[m.lineId] ?? ""}
                  onChange={(v) => setReason(m.lineId, v)}
                />
              ))}
            </section>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={commit.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                commit.isPending ||
                previewLoading ||
                !selectedPodSize ||
                blockedByMapping ||
                !allMismatchReasonsValid
              }
            >
              {commit.isPending ? "Committing…" : "Commit"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function MismatchReasonRow({
  mismatch,
  value,
  onChange,
}: {
  mismatch: CommitMismatch;
  value: string;
  onChange: (value: string) => void;
}) {
  const trimmed = value.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < 10;
  const inputId = `mismatch-reason-${mismatch.lineId}`;
  return (
    <div className="space-y-1 rounded-md border border-warning-200 bg-warning-50/60 px-3 py-2 dark:border-warning-900/40 dark:bg-warning-950/20">
      <Label htmlFor={inputId} className="flex flex-wrap items-center gap-1.5 text-[12px]">
        <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
          {mismatch.role}
        </span>
        <span className="font-medium">{mismatch.resourceName ?? "Resource"}</span>
        <span className="text-[10px] font-normal text-muted-foreground">
          assigned {mismatch.assignmentDisciplineCode ?? "—"} · expected{" "}
          {mismatch.expectedDisciplineCode ?? "—"}
        </span>
      </Label>
      <Textarea
        id={inputId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Explain why this mismatch is acceptable…"
        rows={2}
        className="text-[12px]"
      />
      {tooShort && <p className="text-[10px] text-destructive">Minimum 10 characters required.</p>}
    </div>
  );
}

function PodPositionRow({ pos }: { pos: PodPositionPreview }) {
  const isSupporting = pos.role === "Supporting";
  return (
    <div className="flex items-center justify-between px-3 py-2">
      <div className="flex items-center gap-2">
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
            isSupporting
              ? "bg-muted text-muted-foreground"
              : "bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
          }`}
        >
          {pos.role}
        </span>
        {pos.disciplineCode && (
          <span className="text-[10px] text-muted-foreground">{pos.disciplineCode}</span>
        )}
        <span className="text-[13px] font-medium">
          {pos.resourceName ?? <span className="text-muted-foreground italic">Open</span>}
        </span>
      </div>
      <span
        className="text-[10px] text-muted-foreground"
        title="Capacity points this Pod position consumes"
      >
        {pos.points} pt{pos.points !== 1 ? "s" : ""}
      </span>
    </div>
  );
}

function PodChangeRow({ change }: { change: PodChangeItem }) {
  if (change.type === "unchanged") {
    return (
      <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
        <span className="w-16 text-[10px] font-semibold">{change.role}</span>
        <span>{change.toResourceName ?? "—"}</span>
        <span className="text-[10px]">(unchanged)</span>
      </div>
    );
  }
  if (change.type === "add") {
    return (
      <div className="flex items-center gap-2 text-[12px]">
        <span className="w-16 text-[10px] font-semibold">{change.role}</span>
        <span className="text-success-600 dark:text-success-400">
          + {change.toResourceName ?? "Open"}
        </span>
        <span className="text-[10px] text-muted-foreground">(new)</span>
      </div>
    );
  }
  if (change.type === "replace") {
    return (
      <div className="flex items-center gap-2 text-[12px]">
        <span className="w-16 text-[10px] font-semibold">{change.role}</span>
        <span className="text-muted-foreground line-through">{change.fromResourceName ?? "—"}</span>
        <span className="text-[10px] text-muted-foreground">→</span>
        <span className="text-foreground">{change.toResourceName ?? "Open"}</span>
      </div>
    );
  }
  // remove
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <span className="w-16 text-[10px] font-semibold">{change.role}</span>
      <span className="text-destructive line-through">{change.fromResourceName ?? "—"}</span>
      <span className="text-[10px] text-muted-foreground">(removed)</span>
    </div>
  );
}
